import { z } from 'zod';

import { ConnectorsDetailsMode } from '../../scenes/Connector/utils';

/**
 * Be aware that LaunchDarkly feature flags will be lowerCamelCased, regardless of how they are named.
 * For example: `FlowBuilderWorkTitle` will be `flowBuilderWorkTitle` and `flag-name` will be `flagName`.
 */
export enum FeatureFlagKey {
  testCanary = 'testCanary',
  flowCostEstimate = 'flowCostEstimate',
  canCreateCustomAnalyticEvents = 'canCreateCustomAnalyticEvents',
  enableV2Payments = 'enableV2Payments',
  canConfigureSignalV2 = 'canConfigureSignalV2',
  canConfigurePreAssistantMessage = 'canConfigurePreAssistantMessage',
  canConfigureAutoRedirectFromSchedulingPrimitive = 'canConfigureAutoRedirectFromSchedulingPrimitive',
  autoCompleteX12 = 'autoCompleteX12',
  deprecateAgentPages = 'deprecateAgentPages',
  enablePntDataSubmission = 'enablePntDataSubmission',
  appointmentVoiceReminders = 'appointmentVoiceReminders',
  enableSidekickAutomation = 'enableSidekickAutomation',
  omniContentEmbedMode = 'omniContentEmbedMode',
  connectorsOptions = 'connectorOptions',
  flowInputAutoTypeOptions = 'flowInputAutoTypeOptions',
  flowLifeCycleOptions = 'flowLifeCycleOptions',
  feedValidationSettings = 'feedValidationSettings',
  feedAlertOptions = 'feedAlertOptions',
  sidekickAiAutofill = 'sidekickAiAutofill',
  clinicalQuestionAssistantTool = 'clinicalQuestionAssistantTool',
  enableSidekickClinicalPacketTab = 'enableSidekickClinicalPacketTab',
  enableSipTriggerSupport = 'enableSipTriggerSupport',
  enableVoiceTriggerFallbackDestination = 'enableVoiceTriggerFallbackDestination',
  scheduledFlowTriggers = 'scheduledFlowTriggers',
  conversationTaskAssignmentGroup = 'conversationTaskAssignmentGroup',
  resourceFirstArchitectureDemo = 'resourceFirstArchitectureDemo',
  canViewRestrictedDashboard = 'canViewRestrictedDashboard',
  transformationDemo = 'transformationDemo',
  networkingOptions = 'networkingOptions',
  flowTagOptions = 'flowTagOptions',
  enableFlowTestCaseGeneration = 'enableFlowTestCaseGeneration',
  taskBasedPricing = 'taskBasedPricing',
  globalTools = 'globalTools',
  flowRunExploreExport = 'flowRunExploreExport',
  flowRunActualCost = 'flowRunActualCost',
  agenticPlatformOptions = 'agenticPlatformOptions',
}

export const FeatureFlagSchemas = {
  [FeatureFlagKey.testCanary]: z.string().default('None'),
  [FeatureFlagKey.flowCostEstimate]: z.boolean().default(false),
  [FeatureFlagKey.canCreateCustomAnalyticEvents]: z.boolean().default(false),
  [FeatureFlagKey.enableV2Payments]: z.boolean().default(false),
  [FeatureFlagKey.canConfigureSignalV2]: z.boolean().default(false),
  [FeatureFlagKey.canConfigurePreAssistantMessage]: z.boolean().default(false),
  [FeatureFlagKey.canConfigureAutoRedirectFromSchedulingPrimitive]: z.boolean().default(false),
  [FeatureFlagKey.autoCompleteX12]: z
    .object({
      isEnabled: z.boolean().default(false),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enablePntDataSubmission]: z
    .object({
      enablePntDataSubmission: z.boolean(),
    })
    .default({
      enablePntDataSubmission: false,
    }),
  [FeatureFlagKey.deprecateAgentPages]: z
    .object({
      flowsPageMode: z.enum(['LegacyAgentsPage', 'NewFlowsPage']).default('LegacyAgentsPage'),
    })
    .default({
      flowsPageMode: 'LegacyAgentsPage',
    }),
  [FeatureFlagKey.appointmentVoiceReminders]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enableSidekickAutomation]: z.boolean().default(false),
  [FeatureFlagKey.omniContentEmbedMode]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.connectorsOptions]: z
    .object({
      isListScreenEnabled: z.boolean(),
      detailsMode: z.enum(ConnectorsDetailsMode),
      isEventsTabVisible: z.boolean(),
      isResourcesTabVisible: z.boolean(),
      isCreateEnabled: z.boolean(),
    })
    .default({
      isListScreenEnabled: false,
      detailsMode: ConnectorsDetailsMode.Drawer,
      isEventsTabVisible: false,
      isResourcesTabVisible: false,
      isCreateEnabled: false,
    }),
  [FeatureFlagKey.flowInputAutoTypeOptions]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowLifeCycleOptions]: z
    .object({
      baseFlowMode: z
        .enum(['UseNewReviewPublishLifeCycle', 'LegacyLifeCycle'])
        .default('LegacyLifeCycle'),
    })
    .default({
      baseFlowMode: 'LegacyLifeCycle',
    }),
  [FeatureFlagKey.feedValidationSettings]: z.boolean().default(false),
  [FeatureFlagKey.feedAlertOptions]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.sidekickAiAutofill]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.clinicalQuestionAssistantTool]: z
    .object({
      isVisibleInFlowBuilder: z.boolean(),
    })
    .default({
      isVisibleInFlowBuilder: false,
    }),
  [FeatureFlagKey.enableSidekickClinicalPacketTab]: z
    .object({
      isEnabled: z.literal(true),
    })
    .or(
      z.object({
        isEnabled: z.literal(false),
      })
    )
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.enableSipTriggerSupport]: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  [FeatureFlagKey.enableVoiceTriggerFallbackDestination]: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  [FeatureFlagKey.scheduledFlowTriggers]: z.boolean().default(false),
  [FeatureFlagKey.conversationTaskAssignmentGroup]: z
    .object({
      isEnabledOnBlock: z.boolean(),
      isEnabledInListPage: z.boolean(),
    })
    .default({ isEnabledOnBlock: true, isEnabledInListPage: true }),
  [FeatureFlagKey.resourceFirstArchitectureDemo]: z.boolean().default(false),
  [FeatureFlagKey.canViewRestrictedDashboard]: z
    .array(
      z.object({
        accessStatus: z.enum(['hasAccess', 'noAccess']),
        restrictedDashboardId: z.string(),
      })
    )
    .default([
      {
        accessStatus: 'noAccess',
        restrictedDashboardId: '019df95a-1099-7a8f-8380-aa46766d6d47',
      },
    ]),
  [FeatureFlagKey.transformationDemo]: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  [FeatureFlagKey.networkingOptions]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowTagOptions]: z
    .object({
      showTagsOnDialog: z.boolean(),
    })
    .default({
      showTagsOnDialog: false,
    }),
  [FeatureFlagKey.taskBasedPricing]: z
    .object({
      isPricingModeEnabled: z.boolean(),
      isSolutionAreaRequiredEnabled: z.boolean(),
      isUseCaseRequiredEnabled: z.boolean(),
    })
    .default({
      isPricingModeEnabled: false,
      isSolutionAreaRequiredEnabled: false,
      isUseCaseRequiredEnabled: false,
    }),
  [FeatureFlagKey.enableFlowTestCaseGeneration]: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  [FeatureFlagKey.globalTools]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowRunExploreExport]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.flowRunActualCost]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
  [FeatureFlagKey.agenticPlatformOptions]: z
    .object({
      isEnabled: z.boolean(),
    })
    .default({
      isEnabled: false,
    }),
} as const;
